import { Dialog, DialogActions, Button, Typography } from '@mui/material';
import { Form } from '@sprytki-frontend-apps/common';
import { useTranslation } from 'react-i18next';
import { DefaultTextField, DictLockerStatus } from 'shared';
import { useLockerOutOfUse } from '../../alerts.logic';
import { StyledDialogContent } from 'components/alerts/StyledComponents';
import { appStore } from 'stores/appStore';
import { DualTitleDialog } from '../DualTitleDialog';
import { observer } from 'mobx-react-lite';

interface OutOfUseDialogProps {
	open: boolean;
	onClose: () => void;
	lockerId: string;
	lockerStatus?: DictLockerStatus;
	userFriendlyId: string;
	typeLabel: string;
	typeTag?: string | null;
}

// Polish needs per-type wording (case + gender agreement); other languages fall back to the {{type}} base string.
const TYPE_CONTEXT: Record<string, string> = {
	'#parkings': 'parkings',
	'#offices': 'offices',
	'#services': 'services',
};

type OutOfUseFormValues = {
	outOfUseReason?: string;
};

export const OutOfUseDialog = observer(
	({ open, onClose, lockerId, lockerStatus, userFriendlyId, typeLabel, typeTag }: OutOfUseDialogProps) => {
		const { t } = useTranslation();
		const isOutOfUse = lockerStatus === DictLockerStatus.OutOfUse;
		const { handleOutOfUse } = useLockerOutOfUse(lockerId, isOutOfUse);

		const typeContext = typeTag ? TYPE_CONTEXT[typeTag] : undefined;
		const typeOptions = { type: typeLabel.toLowerCase(), ...(typeContext ? { context: typeContext } : {}) };

		const title = t(isOutOfUse ? 'dialogs.outOfUse.enableTitle' : 'dialogs.outOfUse.disableTitle', typeOptions);
		const description = t(
			isOutOfUse ? 'dialogs.outOfUse.enableDescription' : 'dialogs.outOfUse.disableDescription',
			typeOptions
		);

		const submitLabel = t(isOutOfUse ? 'common.buttons.putInToUseShort' : 'common.buttons.disable');

		const onSubmit = async ({ outOfUseReason }: OutOfUseFormValues) => {
			const reason = outOfUseReason?.trim();
			const isSuccess = await handleOutOfUse(reason);
			if (isSuccess) {
				onClose();
			}
		};

		return (
			<Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
				<Form onSubmit={onSubmit} resetOnSubmit>
					<DualTitleDialog
						dialogTitle={title}
						dialogTitleSecondary={`${typeLabel}: ${userFriendlyId}`}
					/>
					<StyledDialogContent>
						<Typography variant='body2' gutterBottom>
							{description}
						</Typography>
						{!isOutOfUse && (
							<DefaultTextField
								name='outOfUseReason'
								label={t('dialogs.outOfUse.reasonLabel')}
								placeholder={t('dialogs.outOfUse.reasonPlaceholder', typeOptions)}
								fullWidth
								multiline
								minRows={3}
								margin='normal'
								disabled={appStore.isLoading}
								inputProps={{ maxLength: 256 }}
							/>
						)}
					</StyledDialogContent>
					<DialogActions>
						<Button variant='outlined' onClick={onClose} disabled={appStore.isLoading}>
							{t('common.cancel')}
						</Button>
						<Button type='submit' variant='contained' color='primary' disabled={appStore.isLoading}>
							{submitLabel}
						</Button>
					</DialogActions>
				</Form>
			</Dialog>
		);
	}
);
